import Form from '@bluheadless/ui-logic/src/components/form/form'
import { useFormFieldsLabels } from '@bluheadless/ui-logic/src/hooks/form/fields-config/useFormFieldsLabels'
import { useFormFields } from '@bluheadless/ui-logic/src/hooks/form/useFormFields'
import { useNewsletter } from '@bluheadless/ui-logic/src/hooks/newsletter/useNewsletter'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useNewsletterContext } from '@bluheadless/ui-logic/src/providers/newsletter'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import router from 'next/router'
import { object, string } from 'prop-types'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { lazy as yupLazy, object as yupObject, string as yupString } from 'yup'
import NewsletterFormFull from './newsletter-form-full'
import { useCountryShippingDefault } from '@bluheadless/ui-logic/src/hooks/useCountryShippingDefault'

const NewsletterForm = ({ className, email, buttonProps, checkboxProps, source, redirect, redirectTo }) => {
	const {
		newsletter: {
			popup: { fields },
		},
	} = useConfig()
	const { action: subscribe } = useNewsletter()
	const [success, setSuccess] = useState(false)
	const { enqueueNotification } = useNotification()
	const { formatMessage } = useIntl()
	const [, { setOpen, setEmail }] = useNewsletterContext()

	const {
		collections: collectionsField,
		country: countryField,
		dob: dobField,
		email: emailField,
		firstName: firstNameField,
		gender: genderField,
		lastName: lastNameField,
		telephone: telephoneField,
		privacyPolicy: privacyPolicyField,
	} = useFormFields()

	const formDefaultValues = {
		[countryField.name]: '',
		[dobField.name]: '',
		[emailField.name]: '',
		[firstNameField.name]: '',
		[genderField.name]: '',
		[lastNameField.name]: '',
		[telephoneField.name]: '',
		[collectionsField.name]: fields.collections.values.reduce(
			(acc, { code }) => ({
				...acc,
				[code]: false,
			}),
			{}
		),
	}

	const { telephone: telephoneLabel } = useFormFieldsLabels()
	const { country: defaultShippingCountry } = useCountryShippingDefault()

	const validationSchema = yupObject().shape(
		{
			[collectionsField.name]: fields.collections.required ? collectionsField.validation : yupObject(),
			[countryField.name]: fields.country.required ? countryField.validation : countryField.validation.notRequired(),
			//[dobField.name]: fields.dob.required ? dobField.validation : dobField.validation.notRequired(),
			[emailField.name]: emailField.validation,
			[firstNameField.name]: fields.firstname.required
				? firstNameField.validation
				: firstNameField.validation.notRequired(),
			[genderField.name]: fields.gender.required ? genderField.validation : genderField.validation.notRequired(),
			[lastNameField.name]: fields.lastname.required
				? lastNameField.validation
				: lastNameField.validation.notRequired(),
			[telephoneField.name]: fields.telephone.required
				? telephoneField.validation
				: yupLazy((value) =>
						value === ''
							? yupString()
							: yupString().telephone({ defaultCountry: defaultShippingCountry?.code }).label(telephoneLabel)
				  ),
			[privacyPolicyField.name]: privacyPolicyField.validation,
		},
		[]
	)

	const handleSubmit = useCallback(
		async (formData) => {
			try {
				const editedFormData = { ...formData, dob: formData?.dob ? dayjs(formData.dob).format('YYYY-MM-DD') : '' }
				await subscribe({
					formData: editedFormData,
					source,
				})
				setOpen(false)
				setSuccess(true)
				setEmail('')
				enqueueNotification({ uiProps: { message: formatMessage({ id: 'form_newsletter_subscribe_success' }) } })
				if (redirect && redirectTo) {
					router.push(redirectTo)
				}
			} catch (err) {
				enqueueNotification({ uiProps: { severity: 'error', message: formatMessage({ id: 'an_error_occurred' }) } })
			}
		},
		[enqueueNotification, formatMessage, redirect, redirectTo, setEmail, setOpen, source, subscribe]
	)

	return (
		<div className={className}>
			<Form
				onSubmit={handleSubmit}
				options={{
					resolver: yupResolver(validationSchema),
					defaultValues: formDefaultValues,
				}}
				noValidate
				captcha
			>
				<NewsletterFormFull email={email} success={success} buttonProps={buttonProps} checkboxProps={checkboxProps} />
			</Form>
		</div>
	)
}

NewsletterForm.propTypes = {
	/**
	 * Defines the properties of the privacy checkbox
	 */
	checkboxProps: object,
	/**
	 * Defines the properties of the subscribe button
	 */
	buttonProps: object,
	/**
	 * Defines the default email
	 */
	email: string,
}

export default NewsletterForm
